/**
 * PickupComponent - provides pickup functionality for actors with trigger physics.
 * Automatically detects trigger-enabled SceneComponents and handles collision-based pickup.
 */

import * as THREE from 'three';

import { Actor } from '../../actors/Actor.js';
import { Pawn } from '../../actors/Pawn.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';
import * as WorldCommands from '../../world-commands/index.js';
import { CharacterStatsComponent } from '../CharacterStatsComponent.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';
import { SceneComponent } from '../SceneComponent.js';
import { ModelMeshComponent } from '../visual/ModelMeshComponent.js';

import type { AudioPath, PrefabPath } from '../../utils/serialization/decorator.js';
import type { SceneComponentOptions } from '../SceneComponent.js';

export interface PickupComponentOptions extends SceneComponentOptions {
  /** Sound configuration */
  sound?: {
    /** URL to the pickup sound file */
    pickupSoundUrl?: string;
    /** Volume of the pickup sound (0.0 to 1.0) */
    volume?: number;
    /** Maximum distance for the pickup sound */
    maxDistance?: number;
  };
}

@EngineClass('PickupComponent')
export class PickupComponent extends SceneComponent {


  protected triggerComponent: PrimitiveComponent | null = null;

  /** Delegate called when this pickup is collected */
  public readonly onPickup: Delegate<[Actor, Actor], void> = new Delegate();

  @property({ description: 'URL to the pickup sound file' })
  protected pickupSoundUrl: AudioPath | undefined = undefined;

  @property({ type: 'number', min: 0, max: 1, step: 0.1, decimals: 2, description: 'Volume of the pickup sound' })
  protected pickupVolume: number = 0.5;

  @property({ type: 'number', min: 0, step: 1, decimals: 0, description: 'Maximum distance for the pickup sound' })
  protected pickupMaxDistance: number = 10;

  public override initialize(options?: PickupComponentOptions): void {
    super.initialize(options);
    if (options?.sound?.pickupSoundUrl !== undefined) this.pickupSoundUrl = options.sound.pickupSoundUrl;
    if (options?.sound?.volume !== undefined) this.pickupVolume = options.sound.volume;
    if (options?.sound?.maxDistance !== undefined) this.pickupMaxDistance = options.sound.maxDistance;
  }

  public override postLoad(): void {
    super.postLoad();
  }

  public override beginPlay(): void {
    super.beginPlay();

    // do it in the next tick to allow physics to be initialized
    this.getWorld()?.runInNextTick(() => {
      this.findTriggerComponent();
    });

    // special handling for ModelMeshComponents which can take some time to load
    const modelMeshComponents = this.getActor()?.getComponents(ModelMeshComponent) ?? [];
    for (const modelMeshComponent of modelMeshComponents) {
      modelMeshComponent.onMeshLoaded.add(() => {
        this.findTriggerComponent();
      });
    }
  }

  protected findTriggerComponent(): void {
    if (this.triggerComponent) {
      return;
    }

    const actor = this.getActor();
    if (!actor) {
      console.warn('[PickupComponent] No actor found');
      return;
    }

    const allPrimitiveComponents: PrimitiveComponent[] = actor.getComponents(PrimitiveComponent);
    for (const primitiveComponent of allPrimitiveComponents) {
      if (!primitiveComponent.getPhysicsOptions()?.generateCollisionEvents) {
        continue;
      }

      const physicsState = this.getPhysicsEngine()?.getComponentState(primitiveComponent);
      if (!physicsState) {
        continue;
      }

      if (physicsState.isTrigger) {
        this.triggerComponent = primitiveComponent;
        break;
      }
    }

    if (!this.triggerComponent) {
      return;
    }

    // Set up overlap detection on the mesh component
    this.triggerComponent.onOverlapWith.add((thisComponent, otherComponent, collisionEvent) => {
      const otherActor = otherComponent.getActor();

      // Check if the pickup can be collected by this actor
      if (otherActor && this.canPickup(otherActor)) {
        this.handlePickup(otherActor);
      }
    });

    console.log('[PickupComponent] Successfully initialized with trigger component');
  }

  /**
   * Determines if the pickup can be collected by the given actor.
   * Override this method in subclasses to implement custom pickup logic.
   * @param actor - The actor attempting to pick up this item
   */
  protected canPickup(actor: Actor): boolean {
    // Default implementation: only Pawns can pick up items
    return actor instanceof Pawn;
  }

  /**
   * Internal method that handles the pickup process.
   * @param collector - The actor that collected this pickup
   */
  private handlePickup(collector: Actor): void {
    const pickupActor = this.getActor();
    if (!pickupActor) return;

    // Invoke the delegate first (before destroying the actor)
    this.onPickup.invoke(pickupActor, collector);

    // Then call the overridable method
    this.onPickupInternal(collector);
  }

  /**
   * Called when the pickup is collected.
   * Override this method in subclasses to implement custom pickup behavior.
   * @param collector - The actor that collected this pickup
   */
  protected onPickupInternal(collector: Actor): void {
    console.log('[PickupComponent] Pickup collected!');

    if (this.pickupSoundUrl) {
      const world = this.getWorld();
      if (world?.globalAudioManager) {
        world.globalAudioManager.playGlobalSound(this.pickupSoundUrl, {
          volume: this.pickupVolume
        });
      }
    } else {
      console.log('[PickupComponent] No pickup sound configured');
    }

    // Remove the pickup actor from the world
    const actor = this.getActor();
    if (actor) {
      actor.destroy();
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Pickup';
  }
}

export interface HealthPickupComponentOptions extends PickupComponentOptions {
  /** Amount of health to restore when picked up */
  healAmount?: number;
}

@EngineClass('HealthPickupComponent')
export class HealthPickupComponent extends PickupComponent {



  @property({ type: 'number', min: 0, step: 1, decimals: 0, description: 'Amount of health to restore' })
  protected healAmount: number = 25;

  public override initialize(options?: HealthPickupComponentOptions): void {
    super.initialize(options);
    this.healAmount = options?.healAmount ?? this.healAmount;
  }

  /** Ensures only Pawns with CharacterStatsComponent can pick up health. */
  protected override canPickup(actor: Actor): boolean {
    // First check if it's a Pawn (base class requirement)
    if (!super.canPickup(actor)) {
      return false;
    }

    // Then check if it has a CharacterStatsComponent
    const pawn = actor as Pawn;
    const statsComponent = pawn.getComponent(CharacterStatsComponent);

    return statsComponent !== null;
  }

  /** Heals the player when picked up. */
  protected override onPickupInternal(collector: Actor): void {
    const player = collector as Pawn;
    const statsComponent = player.getComponent(CharacterStatsComponent);

    if (statsComponent) {
      const currentHealth = statsComponent.getCurrentHealth();
      const maxHealth = statsComponent.getMaxHealth();

      // Heal the player
      const actualHealed = statsComponent.heal(this.healAmount);

      console.log(`[HealthPickupComponent] Healed player: ${currentHealth}/${maxHealth} -> ${statsComponent.getCurrentHealth()}/${maxHealth} (healed ${actualHealed} HP)`);
    }

    // Call the base pickup behavior (destroy)
    super.onPickupInternal(collector);
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_HealthPickup';
  }
}

export interface PickupSpawnerComponentOptions extends SceneComponentOptions {
  /** Path to the prefab to spawn */
  prefabPath?: string;
  /** Spawn interval in seconds. If negative, only spawns once */
  spawnInterval?: number;
  /** Offset position relative to the spawner where pickups should appear */
  spawnOffset?: THREE.Vector3;
  /** Sound configuration for spawning */
  sound?: {
    /** URL to the spawn sound file */
    spawnSoundUrl?: string;
    /** Volume of the spawn sound (0.0 to 1.0) */
    volume?: number;
    /** Maximum distance for the spawn sound */
    maxDistance?: number;
  };
}

@EngineClass('PickupSpawnerComponent')
export class PickupSpawnerComponent extends SceneComponent {


  private currentPickup: Actor | null = null;
  private spawnTimerId: number | null = null;

  @property({ description: 'Path to the prefab to spawn' })
  protected prefabPath: PrefabPath = '@engine/assets/prefabs/actors/demo_health_pickup.prefab.json';

  @property({ type: 'number', min: -1, step: 0.5, decimals: 1, description: 'Spawn interval in seconds (-1 for single spawn)' })
  protected spawnInterval: number = 5.0;

  @property({ description: 'Offset position for spawned pickups' })
  protected spawnOffset: THREE.Vector3 = new THREE.Vector3(0, 2, 0);

  @property({ type: 'audioPath', description: 'URL to the spawn sound file' })
  protected spawnSoundUrl: string | undefined = undefined;

  @property({ type: 'number', min: 0, max: 1, step: 0.1, decimals: 2, description: 'Volume of the spawn sound' })
  protected spawnVolume: number = 0.5;

  @property({ type: 'number', min: 0, step: 1, decimals: 0, description: 'Maximum distance for the spawn sound' })
  protected spawnMaxDistance: number = 15;

  public override initialize(options?: PickupSpawnerComponentOptions): void {
    super.initialize(options);
    if (options?.spawnOffset !== undefined) this.spawnOffset = options.spawnOffset.clone();
    this.prefabPath = options?.prefabPath ?? this.prefabPath;
    this.spawnInterval = options?.spawnInterval ?? this.spawnInterval;
    this.spawnSoundUrl = options?.sound?.spawnSoundUrl ?? this.spawnSoundUrl;
    this.spawnVolume = options?.sound?.volume ?? this.spawnVolume;
    this.spawnMaxDistance = options?.sound?.maxDistance ?? this.spawnMaxDistance;
  }

  public override beginPlay(): void {
    super.beginPlay();

    console.log('[PickupSpawnerComponent] Starting pickup spawner');

    // Spawn the initial pickup
    this.spawnPickup();
  }

  public override endPlay(): void {
    super.endPlay();

    // Clean up timer
    if (this.spawnTimerId !== null) {
      const world = this.getWorld();
      if (world) {
        world.timerSystem.clearTimer(this.spawnTimerId);
        this.spawnTimerId = null;
      }
    }
  }

  private async spawnPickup(): Promise<void> {
    const world = this.getWorld();
    const actor = this.getActor();

    if (!world || !actor) {
      console.warn('[PickupSpawnerComponent] No world or actor found');
      return;
    }

    if (!this.prefabPath) {
      console.warn('[PickupSpawnerComponent] No prefab path specified');
      return;
    }

    try {
      // Calculate spawn position
      const spawnerPosition = actor.rootComponent.getWorldPosition();
      const spawnPosition = spawnerPosition.clone().add(this.spawnOffset);

      // Spawn the pickup using WorldCommands
      const pickupActors = await WorldCommands.placePrefabs({
        world: world,
        prefabs: [
          {
            path: this.prefabPath,
            transform: {
              position: spawnPosition,
              rotation: new THREE.Euler(0, 0, 0)
            }
          }
        ]
      });

      if (pickupActors.length > 0) {
        this.currentPickup = pickupActors[0];

        // Find the pickup component and bind to its onPickup delegate
        const pickupComponent = this.currentPickup.getComponent(PickupComponent);
        if (pickupComponent) {
          pickupComponent.onPickup.add((pickupActor, collector) => {
            this.onPickupCollected(pickupActor, collector);
          });
        }

        // Play spawn sound if configured
        if (this.spawnSoundUrl) {
          world.globalAudioManager?.playGlobalSound(this.spawnSoundUrl, {
            volume: this.spawnVolume
          });
        }

        console.log('[PickupSpawnerComponent] Spawned pickup at:', spawnPosition);
      }
    } catch (error) {
      console.error('[PickupSpawnerComponent] Failed to spawn pickup:', error);
    }
  }

  private onPickupCollected(pickupActor: Actor, collector: Actor): void {
    console.log('[PickupSpawnerComponent] Pickup was collected');

    this.currentPickup = null;

    // Only respawn if interval is non-negative
    if (this.spawnInterval >= 0) {
      const world = this.getWorld();
      if (world) {
        // Schedule the next spawn
        this.spawnTimerId = world.timerSystem.setTimeout(() => {
          this.spawnTimerId = null;
          this.spawnPickup();
        }, this.spawnInterval);

        console.log(`[PickupSpawnerComponent] Next pickup will spawn in ${this.spawnInterval} seconds`);
      }
    } else {
      console.log('[PickupSpawnerComponent] Single-use spawner, will not respawn');
    }
  }

  /** Manually triggers a pickup spawn if no pickup is currently active. */
  public manualSpawn(): void {
    if (!this.currentPickup) {
      this.spawnPickup();
    } else {
      console.log('[PickupSpawnerComponent] Cannot spawn, pickup already exists');
    }
  }

  /** Gets the currently spawned pickup actor. */
  public getCurrentPickup(): Actor | null {
    return this.currentPickup;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Pickup';
  }
}
